import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useState } from 'react';
import { Elements, StripeProvider } from 'react-stripe-elements';
import styled from 'styled-components';
import LoadingAnimation from '../../../../components/LoadingAnimation';
import PageWithAuth from '../../../../components/PageWithAuth';
import { googleFontSrcNunito, stripePublicKey } from '../../../../constants';
import pagesMeta from '../../../../constants/pages-meta';
import { useUser } from '../../../../utils/auth-client';
import ConfirmResumeQuizModal from '../../components/ConfirmResumeQuizModal';
import { QuizContextProvider, useIsLoading } from '../../context/QuizDataContext';
import useRedirectToShop from '../../hooks/useRedirectToShop';
import useReturnToLastStep from '../../hooks/useReturnToLastStep';
import SignUpPage from '../SignUpPage';

const QuizWizard = dynamic(() => import('../QuizWizard'), {
  ssr: false,
  loading: () => <LoadingAnimation />,
});

const CenteredLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 400px;
`;

const QuizPage = () => {
  const [stripe, setStripe] = useState(null);
  const { currentUser, isLoading } = useUser();
  const { isRedirecting, isQuizRedirectionEnabled } = useRedirectToShop();
  const [openResumeQuizModal, setOpenResumeQuizModal] = useState({ open: false, verifiedOnce: false });
  const isQuizLoading = useIsLoading();

  // return to current step automatically
  const { incompleteKids, lastKidName } = useReturnToLastStep();

  useEffect(() => {
    if (incompleteKids.length > 0) {
      setOpenResumeQuizModal(prev => ({ open: true, verifiedOnce: prev.verifiedOnce }));
      return;
    }
    if (!isQuizLoading) {
      setOpenResumeQuizModal({ open: false, verifiedOnce: true });
    }
  }, [incompleteKids.length, isQuizLoading]);

  const closeResumeQuizModal = useCallback(() => setOpenResumeQuizModal({ open: false, verifiedOnce: true }), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.Stripe && setStripe(window.Stripe(stripePublicKey));
  }, []);

  if (isLoading || (isRedirecting && isQuizRedirectionEnabled && !currentUser)) {
    return (
      <CenteredLayout>
        <LoadingAnimation />
      </CenteredLayout>
    );
  }

  if (!currentUser && !isQuizRedirectionEnabled) {
    return <SignUpPage />;
  }

  return (
    <>
      <CenteredLayout>
        <StripeProvider stripe={stripe}>
          <Elements fonts={[{ cssSrc: googleFontSrcNunito }]}>
            <QuizWizard />
          </Elements>
        </StripeProvider>
      </CenteredLayout>

      <ConfirmResumeQuizModal
        incompleteKids={incompleteKids}
        open={openResumeQuizModal.open && !openResumeQuizModal.verifiedOnce}
        onClose={closeResumeQuizModal}
        currentKidName={(lastKidName as string) || incompleteKids[0]?.name}
      />
    </>
  );
};

const QuizPageWithAuth = () => (
  <PageWithAuth title={pagesMeta.quiz.title} description={pagesMeta.quiz.description}>
    <QuizContextProvider>
      <QuizPage />
    </QuizContextProvider>
  </PageWithAuth>
);

export default QuizPageWithAuth;
